import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { CashReceiptPaymentCaptureTableModel } from 'src/app/models/tables/wallet/cash-receipt-payment-capture-table.model';
import { CashReceiptPaymentModel } from 'src/app/models/wallet/cash-receipt-payment.model';
import { CashReceiptModel } from 'src/app/models/wallet/cash-receipt.model';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { CashReceiptPaymentService } from 'src/app/services/wallet/cash-receipt-payment.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-cash-receipt-payment',
  templateUrl: './cash-receipt-payment.component.html',
  styleUrls: ['./cash-receipt-payment.component.scss'],
  standalone: false
})
export class CashReceiptPaymentComponent implements OnInit, OnChanges {

  @Input() cashReceiptModel: CashReceiptModel = new CashReceiptModel();
  @Input() disabledDetail: boolean = false;
  @Input() permissionUpdatable: boolean = true;
  @Input() updatePayments: boolean = false;
  @Output() quantityDetails = new EventEmitter<number>();
  @Output() updateCashReceipt = new EventEmitter<boolean>();

  ascendant: boolean = true;
  cashReceiptPaymentContract: CashReceiptPaymentModel = new CashReceiptPaymentModel();
  columnSort: string | null = null;
  formCashReceiptPayment: FormGroup = new FormGroup({});
  isFormView: boolean = false;
  limit: number = 10;
  listWayToPay: any[] = [];
  page: number = 1;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  paymentData: PagedResponseModel<CashReceiptPaymentCaptureTableModel> = new PagedResponseModel<CashReceiptPaymentCaptureTableModel>();
  paymentHeader: ColumnResponseModel[] = [];
  tableActions: TableAction[] = [];

  constructor(private cashReceiptPaymentService: CashReceiptPaymentService,
    private formBuilder: FormBuilder,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(this.cashReceiptPaymentContract);
  }

  async ngOnInit() {
    await this.getColumns();
    await this.getListWayToPay();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['cashReceiptModel'] && !changes['cashReceiptModel'].firstChange) ||
      (changes['updatePayments'] && !changes['updatePayments'].firstChange)) {
      if (this.cashReceiptModel.cashReceiptId !== 0 && this.cashReceiptModel.cashReceiptId != null) {
        this.getCashReceiptPayments();
      } else {
        this.paymentData = new PagedResponseModel<CashReceiptPaymentCaptureTableModel>();
      }
    }
    if (changes['disabledDetail']) {
      if (!this.disabledDetail && this.permissionUpdatable) {
        this.tableActions = [
          {
            label: 'Editar',
            icon: 'pi pi-pencil',
            action: 'edit',
          },
        ];
        this.formCashReceiptPayment.enable();
      } else {
        this.tableActions = [];
        this.formCashReceiptPayment.disable();
      }
    }
  }

  changeWayToPay(wayToPayId: number) {
    this.getCashReceiptPaymentByKeys(wayToPayId);
  }

  cancel() {
    this.cashReceiptPaymentContract = new CashReceiptPaymentModel();
    this.validateForm(this.cashReceiptPaymentContract);
    this.getFormView(false);
  }

  deleteCashReceiptPayment(cashReceiptPaymentId: number) {
    if (this.permissionUpdatable) {
      Swal.fire({
        title: 'Atención',
        text: "¿Realmente desea eliminar la forma de pago?",
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Si',
        cancelButtonText: 'No'
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await firstValueFrom(this.cashReceiptPaymentService.deleteCashReceiptPayment(cashReceiptPaymentId));
            this.getCashReceiptPayments();
            this.cancel();
            this.getFormView(false);
            this.updateCashReceipt.emit(true);
            this.toastHandlerService.showToast(
              'success',
              'La forma de pago fue eliminada exitosamente.'
            );
          } catch (error) {
            console.error("Error al eliminar la forma de pago: ", error);
          }
        }
      });
    } else {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para modificar recibos de caja. Comuníquese con el administrador del sistema.'
      );
      return;
    }
  }

  async getCashReceiptPaymentByKeys(wayToPayId: number) {
    if (wayToPayId !== 0) {
      try {
        this.cashReceiptPaymentContract = await firstValueFrom(
          this.cashReceiptPaymentService.getCashReceiptPaymentByKeys(this.cashReceiptModel.cashReceiptId, wayToPayId)
        );
        this.validateForm(this.cashReceiptPaymentContract);
      } catch (error) {
        this.cashReceiptPaymentContract = new CashReceiptPaymentModel();
        this.cashReceiptPaymentContract.wayToPayId = wayToPayId;
        this.validateForm(this.cashReceiptPaymentContract);
        console.error('Error al buscar la forma de pago del recibo de caja por claves.', error);
      }
    }
  }

  async getCashReceiptPayments() {
    try {
      this.paymentData = await firstValueFrom(this.cashReceiptPaymentService.getCashReceiptPaymentsByCashReceiptId(
        this.cashReceiptModel.cashReceiptId, this.page, this.limit, this.columnSort, this.ascendant
      ));
      this.quantityDetails.emit(this.paymentData.items.length);
    } catch (error) {
      console.error('Error al buscar las formas de pago del recibo de caja.', error);
    }
  }

  async getColumns() {
    try {
      this.paymentHeader = await firstValueFrom(this.cashReceiptPaymentService.getColumnsForCaptureTable());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  async getListWayToPay() {
    try {
      this.listWayToPay = await firstValueFrom(this.cashReceiptPaymentService.getAllWayToPaysForSelectAsync());
    } catch (error) {
      console.error('Error al buscar las formas de pago.', error);
    }
  }

  async onAction(event: { action: string, item?: CashReceiptPaymentCaptureTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      if (event.item && event.item.paymentMethod === 0) {
        this.toastHandlerService.showToast(
          'error',
          'No se puede editar la forma de pago Anticipos.'
        );
        return;
      }
      this.cashReceiptPaymentContract = await firstValueFrom(
        this.cashReceiptPaymentService.getCashReceiptPaymentById(event.item.id)
      );
      this.validateForm(this.cashReceiptPaymentContract);
      this.getFormView(true);
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteSomeCashReceiptPayments(event.selectedItems);
    }
  }

  async deleteSomeCashReceiptPayments(selectedIds: number[]) {
    if (selectedIds.length === 0) return;

    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para modificar recibos de caja. Comuníquese con el administrador del sistema.'
      );
      return;
    }

    const hasAdvance = this.paymentData.items.some(item => selectedIds.includes(item.id) && item.paymentMethod === 0);
    if (hasAdvance) {
      this.toastHandlerService.showToast(
        'error',
        'No se puede eliminar la forma de pago Anticipos.'
      );
      return;
    }

    const result = await Swal.fire({
      title: 'Atención',
      text: "¿Realmente desea eliminar las formas de pago seleccionadas?",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si',
      cancelButtonText: 'No'
    });
    if (result.isConfirmed) {
      try {
        await Promise.all(
          selectedIds.map(id => firstValueFrom(this.cashReceiptPaymentService.deleteCashReceiptPayment(id)))
        );
        this.toastHandlerService.showToast(
          'success',
          'Las formas de pago fueron eliminadas exitosamente.'
        );
        this.clearSelectionTrigger++;
        this.getCashReceiptPayments();
        this.cancel();
        this.getFormView(false);
        this.updateCashReceipt.emit(true);
      } catch (error) {
        console.error('Error al eliminar las formas de pago.', error);
      }
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getCashReceiptPayments();
  }

  async send() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para modificar recibos de caja. Comuníquese con el administrador del sistema.');
      return;
    }
    Object.keys(this.formCashReceiptPayment.controls).forEach(key => {
      const control = this.formCashReceiptPayment.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formCashReceiptPayment.valid) {
      const modelCashReceiptPayment = this.formCashReceiptPayment.getRawValue() as CashReceiptPaymentModel;
      modelCashReceiptPayment.cashReceiptId = this.cashReceiptModel.cashReceiptId;

      if (this.cashReceiptPaymentContract.cashReceiptPaymentId == 0) {
        try {
          this.cashReceiptPaymentContract = await firstValueFrom(
            this.cashReceiptPaymentService.createCashReceiptPayment(this.cashReceiptModel.cashReceiptId, modelCashReceiptPayment)
          );
          this.validateForm(this.cashReceiptPaymentContract);
          this.getFormView(false);
          this.toastHandlerService.showToast(
            'success',
            'La forma de pago fue creada exitosamente.'
          );
          this.getCashReceiptPayments();
          this.updateCashReceipt.emit(true);
        } catch (error) {
          console.error('Error al crear la forma de pago del recibo de caja.', error);
        }
      } else {
        this.cashReceiptPaymentContract.paidValue = modelCashReceiptPayment.paidValue;

        try {
          this.cashReceiptPaymentContract = await firstValueFrom(
            this.cashReceiptPaymentService.updateCashReceiptPayment(this.cashReceiptPaymentContract)
          );
          this.validateForm(this.cashReceiptPaymentContract);
          this.toastHandlerService.showToast(
            'success',
            'La forma de pago fue modificada exitosamente.'
          );
          this.getCashReceiptPayments();
          this.getFormView(false);
          this.updateCashReceipt.emit(true);
        } catch (error) {
          console.error('Error al actualizar la forma de pago del recibo de caja.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      wayToPayId: {
        required: 'El campo Forma de Pago es obligatorio.',
        min: 'Debe seleccionar una Forma de Pago válida.'
      },
      paidValue: {
        required: 'El campo Valor de Pago es obligatorio.',
        min: 'El Valor de Pago debe ser mayor o igual a 0.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formCashReceiptPayment.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm(cashReceiptPayment: CashReceiptPaymentModel) {
    this.formCashReceiptPayment = this.formBuilder.group({
      wayToPayId: [cashReceiptPayment.wayToPayId, [Validators.required, Validators.min(1)]],
      paidValue: [cashReceiptPayment.paidValue, [Validators.required, Validators.min(0)]]
    });
  }

}
